import type { ReactNode } from "react"
import { LockKeyhole, Sparkles } from "lucide-react"

import type {
  GalleryStudioFormat,
  GalleryStudioPlanInputs,
  GalleryStudioPlanSlot,
} from "../../services/use-gallery-studio"
import { useGalleryStudioDocumentCandidates } from "../../services/use-gallery-studio"
import { cn } from "../../lib/utils"
import { FormField } from "../ui/form-field"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import { TextInput } from "../ui/text-input"

type IntentSlot = GalleryStudioPlanInputs["intent"]["slots"][number]

export function GalleryStudioSlotSection({
  configSku,
  slot,
  editor,
  active,
  planInputs,
  drafts,
  onActivate,
  onChange,
}: {
  configSku: string
  slot: IntentSlot
  editor: GalleryStudioPlanSlot | null
  active: boolean
  planInputs: GalleryStudioPlanInputs
  drafts: GalleryStudioPlanInputs["existingDrafts"]
  onActivate: () => void
  onChange: (next: GalleryStudioPlanSlot) => void
}) {
  const slotDrafts = drafts.filter((draft) => draft.slotId === slot.id)
  const isMain = slot.kind === "main"
  const subtitle = slotSubtitle(isMain, editor, planInputs.intent.targetSimpleSkus.length)
  let slotBody: ReactNode
  if (isMain) {
    slotBody = (
      <div className="border-t border-border/70 px-5 py-4 text-sm text-muted-foreground">
        Main stays first and cannot be generated or edited here. It anchors every marketplace
        gallery.
      </div>
    )
  } else if (active && editor) {
    slotBody = <SlotSettings editor={editor} planInputs={planInputs} onChange={onChange} />
  } else {
    slotBody = (
      <div className="border-t border-border/70 px-5 py-4">
        <p className="line-clamp-2 max-w-3xl text-sm text-foreground">
          {editor?.brief ?? "Open this Slot to add its generation brief and structured copy."}
        </p>

        {editor ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {[...editor.formats, ...editor.marketplaces].join(" · ")}
          </p>
        ) : null}
      </div>
    )
  }
  return (
    <section
      id={`gallery-slot-${slot.id}`}
      data-gallery-slot={slot.id}
      aria-labelledby={`gallery-slot-${slot.id}-title`}
      className={cn(
        "scroll-mt-28 rounded-xl border bg-card shadow-card transition-[border-color] duration-200",
        active ? "border-foreground/20" : "border-border"
      )}
    >
      <button
        type="button"
        onClick={onActivate}
        aria-expanded={active}
        className="flex w-full items-start justify-between gap-4 rounded-xl px-5 py-4 text-left outline-none transition-colors hover:bg-muted/35 focus-visible:ring-2 focus-visible:ring-ring/30"
      >
        <span>
          <span
            id={`gallery-slot-${slot.id}-title`}
            className="block text-[15px] font-semibold tracking-tight"
          >
            {slot.name}
          </span>

          <span className="mt-1 block text-xs text-muted-foreground">{subtitle}</span>
        </span>

        <span className="flex shrink-0 items-center gap-2">
          {isMain ? (
            <StatusBadge size="sm">
              <LockKeyhole className="size-3" /> Locked
            </StatusBadge>
          ) : null}

          {slotDrafts.length > 0 ? (
            <StatusBadge size="sm" tone="success">
              {slotDrafts.length} drafts
            </StatusBadge>
          ) : null}

          {active ? (
            <StatusBadge size="sm" tone="info">
              Active
            </StatusBadge>
          ) : null}
        </span>
      </button>

      {slotBody}

      {!isMain ? <CandidateStrip configSku={configSku} drafts={slotDrafts} /> : null}
    </section>
  )
}

function SlotSettings({
  editor,
  planInputs,
  onChange,
}: {
  editor: GalleryStudioPlanSlot
  planInputs: GalleryStudioPlanInputs
  onChange: (next: GalleryStudioPlanSlot) => void
}) {
  const fieldId = (name: string) => `${editor.slotId}-${name}`
  const setCopy = (field: keyof GalleryStudioPlanSlot["copy"], value: string) =>
    onChange({ ...editor, copy: { ...editor.copy, [field]: value } })
  return (
    <div className="space-y-6 border-t border-border/70 px-5 py-5">
      <FormField id={fieldId("name")} label="Slot name">
        {(props) => (
          <TextInput
            {...props}
            id={fieldId("name")}
            value={editor.name}
            onChange={(event) => onChange({ ...editor, name: event.target.value })}
          />
        )}
      </FormField>

      <FormField
        id={fieldId("brief")}
        label="Generation brief"
        hint="Describe the scene and product story. Layer editing stays outside this mode."
      >
        {(props) => (
          <textarea
            {...props}
            id={fieldId("brief")}
            value={editor.brief}
            onChange={(event) => onChange({ ...editor, brief: event.target.value })}
            rows={4}
            className="w-full resize-y rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground shadow-card outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground hover:border-foreground/15 focus-visible:ring-2 focus-visible:ring-ring/30"
          />
        )}
      </FormField>

      <div className="grid gap-4 md:grid-cols-2">
        {(["headline", "subheadline", "benefit", "trust"] as const).map((field) => (
          <FormField key={field} id={fieldId(field)} label={copyLabel(field)}>
            {(props) => (
              <TextInput
                {...props}
                id={fieldId(field)}
                value={editor.copy[field]}
                onChange={(event) => setCopy(field, event.target.value)}
              />
            )}
          </FormField>
        ))}
      </div>

      <ChoiceFieldset label="Marketplaces" hint={`${planInputs.store} contracts only`}>
        {planInputs.marketplaces.map((marketplace) => (
          <Choice
            key={marketplace}
            label={marketplace}
            checked={editor.marketplaces.includes(marketplace)}
            onChange={() =>
              onChange({
                ...editor,
                marketplaces: toggleValue(editor.marketplaces, marketplace),
              })
            }
          />
        ))}
      </ChoiceFieldset>

      <ChoiceFieldset
        label="Product variants"
        hint={`${planInputs.intent.targetSimpleSkus.length} frozen intent variants`}
      >
        {planInputs.productFacts.map((product) => {
          const hasIdentity = planInputs.currentIdentityContracts.some(
            (identity) => identity.simpleSku === product.simpleSku && identity.status === "approved"
          )
          return (
            <Choice
              key={product.simpleSku}
              label={`${product.name ?? product.simpleSku} · ${product.simpleSku}${hasIdentity ? " · approved identity" : " · identity setup needed"}`}
              checked={editor.simpleSkus.includes(product.simpleSku)}
              onChange={() =>
                onChange({
                  ...editor,
                  simpleSkus: toggleValue(editor.simpleSkus, product.simpleSku),
                })
              }
            />
          )
        })}
      </ChoiceFieldset>

      <ChoiceFieldset
        label="Generation references"
        hint="Exact assets from the selected immutable Visual Direction version"
      >
        {planInputs.generationReferences.length > 0 ? (
          planInputs.generationReferences.map((asset) => (
            <Choice
              key={asset.id}
              label={`${asset.name} · ${asset.kind}`}
              checked={editor.visualDirectionReferenceAssetIds.includes(asset.id)}
              onChange={() =>
                onChange({
                  ...editor,
                  visualDirectionReferenceAssetIds: toggleValue(
                    editor.visualDirectionReferenceAssetIds,
                    asset.id
                  ),
                })
              }
            />
          ))
        ) : (
          <span className="text-xs text-muted-foreground">No usable generation references.</span>
        )}
      </ChoiceFieldset>

      <fieldset>
        <legend className="text-[13px] font-medium text-foreground">Formats</legend>

        <div className="mt-2 flex flex-wrap gap-2">
          {(["square", "portrait", "landscape"] as GalleryStudioFormat[]).map((format) => {
            const selected = editor.formats.includes(format)
            return (
              <label
                key={format}
                className={cn(
                  "inline-flex cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 text-[13px] transition-colors",
                  selected
                    ? "bg-card text-foreground shadow-card ring-1 ring-border"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() =>
                    onChange({
                      ...editor,
                      formats: selected
                        ? editor.formats.filter((candidate) => candidate !== format)
                        : [...editor.formats, format],
                    })
                  }
                  className="size-3.5 accent-info"
                />

                {formatLabel(format)}
              </label>
            )
          })}
        </div>
      </fieldset>
    </div>
  )
}

function CandidateStrip({
  configSku,
  drafts,
}: {
  configSku: string
  drafts: GalleryStudioPlanInputs["existingDrafts"]
}) {
  const hasCandidates = drafts.some((draft) => draft.candidates.length > 0)
  return (
    <div className="border-t border-border/70 px-5 py-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Sparkles className="size-3.5" /> Candidates
      </div>

      {hasCandidates ? (
        <div className="space-y-4" aria-label="Generated candidates">
          {drafts
            .filter((draft) => draft.candidates.length > 0)
            .map((draft) => (
              <CandidateHistory key={draft.id} configSku={configSku} draft={draft} />
            ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {drafts.length > 0
            ? "Drafts exist without a retained candidate preview."
            : "No candidates yet."}
        </p>
      )}
    </div>
  )
}

function CandidateHistory({
  configSku,
  draft,
}: {
  configSku: string
  draft: GalleryStudioPlanInputs["existingDrafts"][number]
}) {
  const history = useGalleryStudioDocumentCandidates(
    configSku,
    draft.id,
    draft.candidates,
    draft.candidateHistoryPage.nextCursor
  )
  return (
    <div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        {`${draft.simpleSku} · ${draft.marketplace} · ${formatLabel(draft.format)}`}
      </p>

      <div className="flex items-start gap-3 overflow-x-auto pb-1">
        {history.data.map((candidate, index) => {
          const isCurrentPreview = index === 0 && candidate.revisionId === draft.revisionId
          return (
            <a
              key={candidate.id}
              href={candidate.url}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "group relative block shrink-0 overflow-hidden rounded-lg bg-muted outline-none ring-1 ring-border focus-visible:ring-2 focus-visible:ring-ring",
                isCurrentPreview ? "w-36" : "w-24"
              )}
              style={{ aspectRatio: `${candidate.width} / ${candidate.height}` }}
              aria-label={
                isCurrentPreview ? "Open current candidate preview" : "Open generated candidate"
              }
            >
              <img
                src={candidate.url}
                alt=""
                className="size-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
              />

              {isCurrentPreview ? (
                <span className="absolute inset-x-2 bottom-2 rounded-md bg-foreground/85 px-2 py-1 text-center text-[11px] font-medium text-background">
                  Current preview
                </span>
              ) : null}
            </a>
          )
        })}
      </div>

      {history.hasNextPage ? (
        <Button
          size="sm"
          className="mt-2"
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {history.isFetchingNextPage ? "Loading candidates…" : "Load more candidates"}
        </Button>
      ) : null}
    </div>
  )
}

function ChoiceFieldset({
  label,
  hint,
  children,
}: {
  label: string
  hint: string
  children: ReactNode
}) {
  return (
    <fieldset>
      <legend className="text-[13px] font-medium text-foreground">{label}</legend>

      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>

      <div className="mt-2 flex flex-wrap gap-2">{children}</div>
    </fieldset>
  )
}

function Choice({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-[13px] transition-colors",
        checked
          ? "bg-card text-foreground shadow-card ring-1 ring-border"
          : "bg-muted text-muted-foreground hover:text-foreground"
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-3.5 accent-info"
      />

      {label}
    </label>
  )
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value)
    ? values.filter((candidate) => candidate !== value)
    : [...values, value]
}

function copyLabel(field: keyof GalleryStudioPlanSlot["copy"]): string {
  return field === "subheadline" ? "Subheadline" : `${field[0]?.toUpperCase()}${field.slice(1)}`
}

function formatLabel(format: GalleryStudioFormat): string {
  return `${format[0]?.toUpperCase()}${format.slice(1)}`
}

function slotSubtitle(
  isMain: boolean,
  editor: GalleryStudioPlanSlot | null,
  targetCount: number
): string {
  if (isMain) {
    return `${targetCount} product variants · fixed source image`
  }
  if (!editor) {
    return "No plan settings yet"
  }
  return `${editor.marketplaces.length} marketplaces · ${editor.formats.length} formats · ${editor.simpleSkus.length} variants`
}
